/**
 * Staff invitations arrive either as a link (`…/invite/XH-8F3K-22QD`) or as a
 * short code someone reads out in a meeting. Both go through here.
 */

const CORE = /^XH[A-Z0-9]{8}$/;

/**
 * Normalise anything the user pastes into the canonical `XH-XXXX-XXXX` form.
 * Returns null when the input cannot be a code.
 */
export function normalizeInviteCode(input: string): string | null {
  let raw = input.trim();
  if (raw.length === 0) return null;

  // A pasted link: take the last non-empty path segment.
  if (raw.includes("/")) {
    const withoutQuery = raw.split(/[?#]/)[0] ?? raw;
    const segments = withoutQuery.split("/").filter(Boolean);
    raw = segments[segments.length - 1] ?? "";
  }

  const compact = raw.replace(/[\s\-_]/g, "").toUpperCase();
  if (compact.length === 0) return null;
  if (CORE.test(compact)) {
    return `${compact.slice(0, 2)}-${compact.slice(2, 6)}-${compact.slice(6, 10)}`;
  }
  // Longer opaque tokens (older links) are passed through untouched.
  return /^[A-Za-z0-9._-]{8,128}$/.test(raw.trim()) ? raw.trim() : null;
}

/** Live formatting while the user types, without fighting the caret. */
export function formatInviteCodeInput(input: string): string {
  if (input.includes("/")) return input;
  const compact = input.replace(/[\s\-_]/g, "").toUpperCase();
  if (!/^[A-Z0-9]*$/.test(compact)) return input.toUpperCase();
  const parts = [compact.slice(0, 2), compact.slice(2, 6), compact.slice(6, 10)].filter(
    (p) => p.length > 0,
  );
  return compact.length > 10 ? compact : parts.join("-");
}
